import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './timestamp-converter.component.module.scss';
import { CodeBlock } from '../converter-layout/code-block';
import { genStyles } from '../converter-layout/generator-layout';

const LANG_TO_PRISM: Record<string, string> = {
    JS: 'javascript', Python: 'python', Java: 'java', PHP: 'php',
    Bash: 'bash', Go: 'go', Rust: 'rust', Ruby: 'ruby',
};

type CodeLang = 'JS' | 'Python' | 'Java' | 'PHP' | 'Bash' | 'Go' | 'Rust' | 'Ruby';

interface TimestampState {
    timestamp: string;
    dateStr: string;
    timeStr: string;
    ms: string;
    date: Date | null;
    error: string;
    copiedField: string | null;
    unit: 'seconds' | 'milliseconds';
    codeLang: CodeLang;
}

function pad(n: number, l = 2): string { return String(n).padStart(l, '0'); }

function relativeTime(date: Date): string {
    const diff = Date.now() - date.getTime();
    const abs = Math.abs(diff);
    const future = diff < 0;
    const suffix = future ? 'from now' : 'ago';
    if (abs < 60000) return `${Math.floor(abs / 1000)} seconds ${suffix}`;
    if (abs < 3600000) return `${Math.floor(abs / 60000)} minutes ${suffix}`;
    if (abs < 86400000) return `${Math.floor(abs / 3600000)} hours ${suffix}`;
    if (abs < 2592000000) return `${Math.floor(abs / 86400000)} days ${suffix}`;
    if (abs < 31536000000) return `${Math.floor(abs / 2592000000)} months ${suffix}`;
    return `${Math.floor(abs / 31536000000)} years ${suffix}`;
}

function detectUnit(val: number): 'seconds' | 'milliseconds' {
    return val > 1e12 ? 'milliseconds' : 'seconds';
}

function toLocalDateStr(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toLocalTimeStr(d: Date): string {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// RFC 2822 / 822 — "Tue, 15 Nov 1994 12:45:26 GMT"
function toRfc2822(d: Date): string {
    return d.toUTCString();
}

// ISO ordinal (YYYY-DDD) — day-of-year format.
function toOrdinal(d: Date): string {
    const start = Date.UTC(d.getUTCFullYear(), 0, 0);
    const diff = d.getTime() - start;
    const day = Math.floor(diff / 86400000);
    return `${d.getUTCFullYear()}-${pad(day, 3)}`;
}

// ISO 8601 week date — YYYY-W[01-53]-[1-7]
function toIsoWeek(d: Date): string {
    const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dow = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - dow);
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((t.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    const isoDow = (d.getUTCDay() || 7);
    return `${t.getUTCFullYear()}-W${pad(week)}-${isoDow}`;
}

// Excel serial date — days since 1899-12-30.
function toExcelSerial(d: Date): string {
    const epoch1900 = Date.UTC(1899, 11, 30);
    const days = (d.getTime() - epoch1900) / 86400000;
    return days.toFixed(5);
}

// Decompose seconds into years/months/days/hours/minutes/seconds.
// Uses average month (30.44 days) for "months" — same convention as
// epochconverter; exact months are calendar-dependent.
function breakdown(totalSeconds: number): { y: number; mo: number; d: number; h: number; m: number; s: number } {
    const abs = Math.abs(totalSeconds);
    const Y = 31557600;       // 365.25 days
    const MO = 2629800;       // 30.4375 days
    const D = 86400;
    const H = 3600;
    const M = 60;
    let r = abs;
    const y = Math.floor(r / Y); r -= y * Y;
    const mo = Math.floor(r / MO); r -= mo * MO;
    const d = Math.floor(r / D); r -= d * D;
    const h = Math.floor(r / H); r -= h * H;
    const m = Math.floor(r / M); r -= m * M;
    const s = Math.floor(r);
    return { y, mo, d, h, m, s };
}

// Boundary timestamps for a given Date (UTC start/end of period).
function boundaries(d: Date): Array<{ label: string; date: Date }> {
    const start = (yr: number, mo: number, day: number) => new Date(Date.UTC(yr, mo, day, 0, 0, 0, 0));
    const end = (yr: number, mo: number, day: number) => new Date(Date.UTC(yr, mo, day, 23, 59, 59, 999));
    const y = d.getUTCFullYear(), mo = d.getUTCMonth(), day = d.getUTCDate();
    const lastDayOfMonth = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
    return [
        { label: 'Start of day (UTC)', date: start(y, mo, day) },
        { label: 'End of day (UTC)', date: end(y, mo, day) },
        { label: 'Start of week (Mon UTC)', date: (() => { const t = start(y, mo, day); const dow = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() - (dow - 1)); return t; })() },
        { label: 'End of week (Sun UTC)', date: (() => { const t = end(y, mo, day); const dow = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + (7 - dow)); return t; })() },
        { label: 'Start of month (UTC)', date: start(y, mo, 1) },
        { label: 'End of month (UTC)', date: end(y, mo, lastDayOfMonth) },
        { label: 'Start of year (UTC)', date: start(y, 0, 1) },
        { label: 'End of year (UTC)', date: end(y, 11, 31) },
    ];
}

function codeSnippet(lang: CodeLang): string {
    switch (lang) {
        case 'JS':     return `// Current epoch (seconds)\nMath.floor(Date.now() / 1000)\n\n// Milliseconds\nDate.now()\n\n// Parse epoch\nnew Date(1700000000 * 1000)`;
        case 'Python': return `# Current epoch (seconds)\nimport time\ntime.time()\n\n# As integer seconds\nint(time.time())\n\n# Parse epoch\nfrom datetime import datetime, timezone\ndatetime.fromtimestamp(1700000000, tz=timezone.utc)`;
        case 'Java':   return `// Current epoch (milliseconds)\nSystem.currentTimeMillis()\n\n// Seconds\nInstant.now().getEpochSecond()\n\n// Parse epoch\nInstant.ofEpochSecond(1700000000L)`;
        case 'PHP':    return `// Current epoch (seconds)\ntime();\n\n// Microseconds (float)\nmicrotime(true);\n\n// Parse epoch\ndate('Y-m-d H:i:s', 1700000000);`;
        case 'Bash':   return `# Current epoch (seconds)\ndate +%s\n\n# Milliseconds (Linux)\ndate +%s%3N\n\n# Parse epoch\ndate -d @1700000000`;
        case 'Go':     return `// Current epoch (seconds)\ntime.Now().Unix()\n\n// Milliseconds\ntime.Now().UnixMilli()\n\n// Parse epoch\ntime.Unix(1700000000, 0)`;
        case 'Rust':   return `// Current epoch (seconds)\nuse std::time::{SystemTime, UNIX_EPOCH};\nSystemTime::now().duration_since(UNIX_EPOCH).unwrap().as_secs()\n\n// chrono:\nchrono::Utc::now().timestamp()`;
        case 'Ruby':   return `# Current epoch (seconds)\nTime.now.to_i\n\n# Milliseconds\n(Time.now.to_f * 1000).to_i\n\n# Parse epoch\nTime.at(1700000000)`;
    }
}

const CODE_LANGS: CodeLang[] = ['JS', 'Python', 'Java', 'PHP', 'Bash', 'Go', 'Rust', 'Ruby'];

export default class UtimestampUconverterComponent extends Component<{ feature?: string }, TimestampState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: TimestampState = {
        timestamp: '', dateStr: '', timeStr: '', ms: '0', date: null, error: '', copiedField: null,
        unit: 'seconds', codeLang: 'JS',
    };

    componentDidMount() {
        this.setNow();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    updateFromTimestamp = (raw: string) => {
        this.setState({ timestamp: raw, error: '' });
        if (!raw.trim()) {
            this.setState({ date: null, dateStr: '', timeStr: '' });
            return;
        }
        const num = Number(raw.trim());
        if (isNaN(num)) {
            this.setState({ error: 'Invalid timestamp', date: null });
            return;
        }
        const unit = detectUnit(num);
        const ms = unit === 'seconds' ? num * 1000 : num;
        const d = new Date(ms);
        if (isNaN(d.getTime())) {
            this.setState({ error: 'Timestamp out of range', date: null });
            return;
        }
        this.setState({ date: d, unit, dateStr: toLocalDateStr(d), timeStr: toLocalTimeStr(d), ms: String(d.getMilliseconds()) });
    };

    updateFromDate = (dateStr: string, timeStr: string, ms?: string) => {
        const msVal = ms ?? this.state.ms;
        this.setState({ dateStr, timeStr, ms: msVal, error: '' });
        const combined = `${dateStr}T${timeStr || '00:00:00'}`;
        const d = new Date(combined);
        if (isNaN(d.getTime())) {
            this.setState({ error: dateStr ? 'Invalid date/time' : '', date: null, timestamp: '' });
            return;
        }
        d.setMilliseconds(Number(msVal) || 0);
        const { unit } = this.state;
        const ts = unit === 'milliseconds' ? d.getTime() : Math.floor(d.getTime() / 1000);
        this.setState({ date: d, timestamp: String(ts) });
    };

    setNow = () => {
        const d = new Date();
        const ts = this.state.unit === 'milliseconds' ? d.getTime() : Math.floor(d.getTime() / 1000);
        this.setState({ date: d, timestamp: String(ts), dateStr: toLocalDateStr(d), timeStr: toLocalTimeStr(d), ms: String(d.getMilliseconds()), error: '' });
    };

    copy = (value: string, field: string) => {
        navigator.clipboard.writeText(value).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: field });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    private epochOf(d: Date): string {
        return this.state.unit === 'milliseconds' ? String(d.getTime()) : String(Math.floor(d.getTime() / 1000));
    }

    render() {
        const { timestamp, dateStr, timeStr, ms, date, error, copiedField, unit, codeLang } = this.state;
        const { feature } = this.props;
        const isTimestampFirst = feature !== 'convert-date-to-timestamp';

        const timestampPanel = (
            <div className={`${styles.col} ${genStyles.section}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className={sharedStyles.label}>Unix Timestamp</label>
                    <select className={sharedStyles.copyButton} value={unit}
                        onChange={e => { this.setState({ unit: e.target.value as 'seconds' | 'milliseconds' }, () => { if (date) { const ts = e.target.value === 'milliseconds' ? date.getTime() : Math.floor(date.getTime() / 1000); this.setState({ timestamp: String(ts) }); } }); }}>
                        <option value="seconds">seconds</option>
                        <option value="milliseconds">milliseconds</option>
                    </select>
                </div>
                <input className={`${styles.input} ${error && !date ? sharedStyles.inputError : ''}`}
                    type="text" placeholder="e.g. 1700000000" value={timestamp}
                    onChange={e => this.updateFromTimestamp(e.target.value)} />
                {error && <div className={sharedStyles.errorMessage}>{error}</div>}
                {date && <div className={styles.detection}>Detected: {unit}</div>}
                <button className={`${sharedStyles.copyButton} ${styles.nowButton}`} onClick={this.setNow}>⏱ Now</button>
            </div>
        );

        const datePanel = (
            <div className={`${styles.col} ${genStyles.section}`}>
                <label className={sharedStyles.label}>Date &amp; Time</label>
                <div className={styles.dateTimeRow}>
                    <input className={styles.input} type="date" value={dateStr} aria-label="Date"
                        onChange={e => this.updateFromDate(e.target.value, timeStr)} />
                    <input className={styles.input} type="time" step="1" value={timeStr} aria-label="Time"
                        onChange={e => this.updateFromDate(dateStr, e.target.value)} />
                </div>
                <div className={styles.msRow}>
                    <label htmlFor="ts-ms" className={styles.msLabel}>Milliseconds</label>
                    <input id="ts-ms" className={styles.input} type="number" inputMode="decimal" min="0" max="999" placeholder="0"
                        value={ms}
                        onChange={e => this.updateFromDate(dateStr, timeStr, e.target.value)} />
                </div>
            </div>
        );

        const formats = date ? [
            { label: 'ISO 8601', value: date.toISOString() },
            { label: 'UTC',      value: date.toUTCString() },
            { label: 'Local',    value: date.toLocaleString() },
            { label: 'RFC 2822', value: toRfc2822(date) },
            { label: 'Ordinal',  value: toOrdinal(date) },
            { label: 'ISO Week', value: toIsoWeek(date) },
            { label: 'Excel',    value: toExcelSerial(date) },
            { label: 'Relative', value: relativeTime(date) },
        ] : [];

        const bks = date ? breakdown(Math.abs(Math.floor(date.getTime() / 1000) - Math.floor(Date.now() / 1000))) : null;
        const bds = date ? boundaries(date) : [];
        const snippet = codeSnippet(codeLang);

        return (
            <div className="row">
                <div className="col-12">
                    <div className={styles.row}>
                        {isTimestampFirst ? <>{timestampPanel}{datePanel}</> : <>{datePanel}{timestampPanel}</>}
                    </div>
                    {formats.length > 0 && (
                        <div className={genStyles.section}>
                            <div className={styles.formats}>
                                {formats.map(f => (
                                    <div key={f.label} className={styles.formatRow}>
                                        <span className={styles.formatLabel}>{f.label}</span>
                                        <span className={styles.formatValue}>{f.value}</span>
                                        <button className={`${sharedStyles.copyButton} ${copiedField === f.label ? sharedStyles.copyButtonSuccess : ''}`}
                                            onClick={() => this.copy(f.value, f.label)}>
                                            {copiedField === f.label ? '✓ Copied' : 'Copy'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {bks && (
                        <div className={`${styles.section} ${genStyles.section}`}>
                            <h2 className={styles.sectionTitle}>Duration breakdown (from now)</h2>
                            <div className={styles.breakdownGrid}>
                                <div className={styles.breakdownCell}><span className={styles.num}>{bks.y}</span><span className={styles.unit}>years</span></div>
                                <div className={styles.breakdownCell}><span className={styles.num}>{bks.mo}</span><span className={styles.unit}>months</span></div>
                                <div className={styles.breakdownCell}><span className={styles.num}>{bks.d}</span><span className={styles.unit}>days</span></div>
                                <div className={styles.breakdownCell}><span className={styles.num}>{bks.h}</span><span className={styles.unit}>hours</span></div>
                                <div className={styles.breakdownCell}><span className={styles.num}>{bks.m}</span><span className={styles.unit}>minutes</span></div>
                                <div className={styles.breakdownCell}><span className={styles.num}>{bks.s}</span><span className={styles.unit}>seconds</span></div>
                            </div>
                        </div>
                    )}

                    {bds.length > 0 && (
                        <div className={`${styles.section} ${genStyles.section}`}>
                            <h2 className={styles.sectionTitle}>Period boundaries</h2>
                            <div className={styles.boundaryGrid}>
                                {bds.map(b => {
                                    const epoch = this.epochOf(b.date);
                                    return (
                                        <div key={b.label} className={styles.boundaryRow}>
                                            <span className={styles.boundaryLabel}>{b.label}</span>
                                            <span className={styles.boundaryValue}>
                                                <span className={styles.ts}>{epoch}</span>
                                                <button className={`${sharedStyles.copyButton} ${copiedField === b.label ? sharedStyles.copyButtonSuccess : ''}`}
                                                    onClick={() => this.copy(epoch, b.label)}>
                                                    {copiedField === b.label ? '✓' : 'Copy'}
                                                </button>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className={`${styles.section} ${genStyles.section}`}>
                        <h2 className={styles.sectionTitle}>Get current epoch in your language</h2>
                        <div className={styles.tabs}>
                            {CODE_LANGS.map(l => (
                                <button key={l} className={`${styles.tab} ${codeLang === l ? styles.tabActive : ''}`}
                                    onClick={() => this.setState({ codeLang: l })}>{l}</button>
                            ))}
                        </div>
                        <CodeBlock code={snippet} language={LANG_TO_PRISM[codeLang] || 'javascript'}
                            copyable wrapLines maxHeight={320} ariaLabel={`${codeLang} epoch snippet`} />
                    </div>
                </div>
            </div>
        );
    }
}
